/**
 * useAndroidBackDismiss — let Android's back button close an overlay instead
 * of navigating away from the screen behind it.
 *
 * useAndroidBackButton dispatches a cancelable `de:androidback` before it
 * touches history; this hook claims that press while an overlay is open.
 *
 *   useAndroidBackDismiss(open, onClose);
 *
 * On Android, back IS Escape — pair it with existing Escape handling.
 *
 * WHY A STACK RATHER THAN ONE LISTENER PER OVERLAY: DOM listeners fire in
 * registration order, so a parent drawer that opened first would claim the
 * press before a nested sheet that opened later — closing the wrong thing.
 * Open overlays instead push onto a module-level stack and the single
 * listener closes the TOP one, so the deepest overlay always wins and one
 * press closes exactly one layer.
 */
import { useEffect, useRef } from 'react';
import { ANDROID_BACK_EVENT } from './useAndroidBackButton';

type Dismiss = () => void;

/** Open overlays, outermost first. The last entry is the deepest. */
const stack: Dismiss[] = [];
let listening = false;

function onBack(e: Event) {
  const top = stack[stack.length - 1];
  if (!top) return;
  e.preventDefault();
  top();
}

function subscribe(fn: Dismiss): () => void {
  stack.push(fn);
  if (!listening) {
    window.addEventListener(ANDROID_BACK_EVENT, onBack);
    listening = true;
  }
  return () => {
    const i = stack.lastIndexOf(fn);
    if (i !== -1) stack.splice(i, 1);
    if (stack.length === 0 && listening) {
      window.removeEventListener(ANDROID_BACK_EVENT, onBack);
      listening = false;
    }
  };
}

export function useAndroidBackDismiss(
  open: boolean,
  onClose: Dismiss,
): void {
  // onClose is almost always an inline arrow at the call site, so its identity
  // changes on every parent render. Depending on it re-ran this effect and
  // re-pushed the entry onto the stack in JSX order, which meant an outer
  // drawer could end up ABOVE a nested sheet and back would close the wrong
  // layer. Hold the callback in a ref and subscribe purely on `open`.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    return subscribe(() => onCloseRef.current());
  }, [open]);
}
